import { Suspense, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  deleteItem,
  fetchPaginationData,
  updateStatus,
} from "../../utils/apiUtils";
import DeleteModel from "../../Components/DeleteModel";
import Breadcrumb from "../../Components/Breadcrumb";
import Pagination from "../../Components/Pagination";
import { FilterButton, PlusButton } from "../../Components/TableHelper";
import TableSearch from "../../Components/TableSearch";
import PerPageSelect from "../../Components/PerPageSelect";
import { useDebounce } from "../../hooks/useDebounce";
import ProductAdvanceFilter from "./components/ProductAdvanceFilter";
import axiosInstance from "../../axiosInterceptor";
import { LayoutGrid, Table2 } from "lucide-react";
import ProductTableView from "./components/ProductTableView";
import ProductCardView from "./components/ProductCardView";
const Products = () => {
  const location = useLocation();
  const navigator = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setdata] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [nextSortOrder, setNextSortOrder] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteItemId, setDeleteItemId] = useState({});
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [perPage, setPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [showFilter, setShowFilter] = useState(false);
  const [categoryData, setCategoryData] = useState([]);
  const [qualityData, setQualityData] = useState([]);
  const [traceabilityData, setTraceabilityData] = useState([]);
  const [packSizeData, setPackSizeData] = useState([]);
  const [lastCategory, setLastCategory] = useState("");

  const [viewMode, setViewMode] = useState("table");
  const [filters, setFilters] = useState({
    categoryId: "",
    qualityId: "",
    traceabilityId: "",
    packSizeId: "",
  });
  const fetchCategories = async () => {
    try {
      const response = await axiosInstance.get(
        "category/getWithoutPaginationCategory",
      );

      setCategoryData(response.data?.data || []);
    } catch (err) {
      setCategoryData([]);
    }
  };
  const fetchDataPagination = async (
    page = 1,
    currentSearch = search,
    currentPerPage = perPage,
    currentFilters = filters,
  ) => {
    const requestData = {
      perPage: currentPerPage,
      pageNo: page,
      search: currentSearch,
      categoryId: currentFilters.categoryId,
      qualityId: currentFilters.qualityId,
      traceabilityId: currentFilters.traceabilityId,
      packSizeId: currentFilters.packSizeId,
    };

    await fetchPaginationData(
      "product/getAllProduct",
      requestData,
      setdata,
      setTotalCount,
      setCurrentPage,
      setLoading,
      setNextSortOrder,
    );
  };
  const fetchByCategory = async (categoryId) => {
    if (!categoryId) {
      setQualityData([]);
      setTraceabilityData([]);
      setPackSizeData([]);
      setLastCategory("");
      return;
    }

    // Same category selected, don't call API again
    if (lastCategory === categoryId) return;

    setLastCategory(categoryId);

    try {
      const response = await axiosInstance.get(
        `category/getCategoryWiseQualityTraceability/${categoryId}`,
      );

      const data = response.data.data;

      setQualityData(data.qualityIds || []);
      setTraceabilityData(data.traceabilityIds || []);
      setPackSizeData(data.packSizeIds || []);
    } catch {
      setQualityData([]);
      setTraceabilityData([]);
      setPackSizeData([]);
    }
  };

  useEffect(() => {
       const page = location.state?.currentPage || 1;
  const perPage = location.state?.perPage || 10;
    setCurrentPage(page);
  setPerPage(perPage);
    fetchDataPagination(page, "", perPage);
    fetchCategories();
  }, []);

  const debouncedFetch = useDebounce((searchValue) => {
    fetchDataPagination(1, searchValue);
  }, 500);

  const handleSearchChange = (value) => {
    setSearch(value);
    debouncedFetch(value);
  };
  const handlePerPageChange = (value) => {
    setPerPage(value);
    fetchDataPagination(1, search, value);
  };
  const totalPages = Math.ceil(totalCount / perPage);
  const handlePageClick = (page) => {
    if (page >= 1 && page <= totalPages) {
      fetchDataPagination(page);
    }
  };

  const handleDelete = async (data) => {
    setDeleteItemId(data);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    setIsDeleting(true);
    try {
      const res = await deleteItem(
        "product/deleteProduct",
        deleteItemId?._id,
        fetchDataPagination,
      );
      setDeleteItemId({});
      setDeleteModalOpen(false);
      return res;
    } catch (error) {
      return error;
    } finally {
      setIsDeleting(false);
    }
  };

  const handleStatusToggle = async (item) => {
    const updated = await updateStatus(item?._id, "product", !item?.isActive);
    if (!updated) return;
    setdata((prev) =>
      prev.map((curr) =>
        curr._id === updated._id
          ? { ...curr, isActive: updated.isActive }
          : curr,
      ),
    );
  };
  const handleAdd = () => {
    navigator("/add-product", { state: {
      sortOrder: nextSortOrder,
      currentPage,
      perPage,
    }, });
  };
  const handleEditClick = (item) => {
    navigator("/add-product", {
     state: {
      ...item,
      currentPage,
      perPage,
    },
    });
  };

  const handleAdvanceSearch = () => {
    fetchDataPagination(1, search, perPage, filters);
  };

  const handleClearFilter = () => {
    const reset = {
      categoryId: "",
      qualityId: "",
      traceabilityId: "",
      packSizeId: "",
    };

    setFilters(reset);

    setQualityData([]);
    setTraceabilityData([]);
    setPackSizeData([]);

    setSearch("");

    fetchDataPagination(1, "", perPage, reset);
  };

  const categoryOptions = categoryData.map((item) => ({
    label: item.categoryName,
    value: item._id,
  }));

  const qualityOptions = qualityData.map((item) => ({
    label: item.qualityName,
    value: item._id,
  }));

  const traceabilityOptions = traceabilityData.map((item) => ({
    label: item.traceabilityName,
    value: item._id,
  }));

  const packSizeOptions = packSizeData.map((item) => ({
    label: item.packSizeName,
    value: item._id,
  }));

  const Tableheader = [
    "Sort",
    "Product Image",
    "Title",
    "SKU",
    "Category",
    "Status",
    "Last Updated",
    "Action",
  ];

  const breadcrumbdata = [
    { label: "Home", path: "/dashboard" },
    { label: "Products", path: "/products" },
  ];

  return (
    <>
      <div className="content-wrapper">
        <div className="page-header">
          <h3 className="custom-page-title">Products</h3>
          <Breadcrumb items={breadcrumbdata} />
        </div>

        <ProductAdvanceFilter
          show={showFilter}
          filters={filters}
          setFilters={setFilters}
          categoryOptions={categoryOptions}
          qualityOptions={qualityOptions}
          traceabilityOptions={traceabilityOptions}
          packSizeOptions={packSizeOptions}
          onCategoryChange={fetchByCategory}
          onSearch={handleAdvanceSearch}
          onClear={handleClearFilter}
        />
        <div className="d-flex justify-content-between align-items-center bg-white p-2 mt-n2 flex-wrap gap-2">
          <div className="d-flex align-items-center gap-2">
            <PerPageSelect value={perPage} onChange={handlePerPageChange} />
          </div>
          <div className="d-flex align-items-center" style={{ gap: "10px" }}>
            <TableSearch value={search} onChange={handleSearchChange} />

            <PlusButton handleAdd={handleAdd} module={"Products"} />
            <FilterButton
              show={showFilter}
              handleClick={() => setShowFilter(!showFilter)}
            />
          <div
  className="view-toggle"
  style={{
    display: "flex",
    alignItems: "center",
    background: "#F8F5F2",
    border: "1px solid #D7CCC8",
    borderRadius: "12px",
    padding: "4px",
    gap: "4px",
    boxShadow: "0 2px 8px rgba(62,39,35,0.08)",
  }}
>
  <button
    className={`view-btn ${viewMode === "table" ? "active" : ""}`}
    onClick={() => setViewMode("table")}
    title="Table View"
  >
    <Table2 size={18} />
    <span>Table</span>
  </button>

  <button
    className={`view-btn ${viewMode === "card" ? "active" : ""}`}
    onClick={() => setViewMode("card")}
    title="Card View"
  >
    <LayoutGrid size={18} />
    <span>Cards</span>
  </button>
</div>
          </div>
        </div>

        <div className="row" style={{ marginTop: "10px" }}>
          <div className="col-md-12 grid-margin stretch-card">
            {viewMode === "table" ? (
              <div className="card">
                <div className="card-body">
                  <ProductTableView
                    loading={loading}
                    data={data}
                    Tableheader={Tableheader}
                    handleStatusToggle={handleStatusToggle}
                    handleEditClick={handleEditClick}
                    handleDelete={handleDelete}
                  />

                  {data.length > 0 && (
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={handlePageClick}
                      perPage={perPage}
                      totalCount={totalCount}
                    />
                  )}
                </div>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column" }}>
                <ProductCardView
                  loading={loading}
                  data={data}
                  handleStatusToggle={handleStatusToggle}
                  handleEditClick={handleEditClick}
                  handleDelete={handleDelete}
                />

                {data.length > 0 && (
                  <div
                    style={{
                      padding: "10px",
                      backgroundColor: "#fff",
                      borderRadius: "12px",
                    }}
                  >
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={handlePageClick}
                      perPage={perPage}
                      totalCount={totalCount}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {deleteModalOpen && (
        <Suspense fallback={null}>
          <DeleteModel
            deleteModalOpen={deleteModalOpen}
            setDeleteModalOpen={() => setDeleteModalOpen(!deleteModalOpen)}
            onConfirm={confirmDelete}
            itemName="Products"
            isDeleting={isDeleting}
          />
        </Suspense>
      )}
    </>
  );
};

export default Products;
